import type { FormEvent } from 'react'
import { Field, reduxForm } from 'redux-form'
import styled from 'styled-components'

import { ShowHelpInputWrapper } from '../claim/HelpInputWrapper'
import { maxSize,required, valid, validator } from '../lib/validators'
import { ContinueButton } from '../shared/ContinueButton'
import { Help } from '../shared/Help'
import { InputWrapper } from '../shared/InputWrapper'
import {
  ButtonContainer,
  Card,
  CardHeader,
  PageInformation,
  SecondaryButton,
  Select,
  Textbox
} from '../shared/styledComponents'
import type { IdentityFormType } from './types'

const roleHelperHeaderText = 'More Information'
const roleHelp = (
  <p>
    Constitution State Services customers should choose “CSS Customer” from the menu.
  </p>
)
const normalize50 = (value: string) => value && value.replace(/[^a-zA-Z0-9 &.`-]/g, '').slice(0, 50)
const normalizeEmail = (value: string) => value && value.replace(/[<>/{}]/g, '')
const renderInput = InputWrapper(Textbox)

const IdentitySelect = styled(Select)`
  margin-left: 31%;
  @media (max-width: 668px) {
    margin-left:0%;
  }
`
const renderSelect = ShowHelpInputWrapper(IdentitySelect, Help, 'role-help')

type IdentityFormProps = {
  handleSubmit(event: React.SyntheticEvent): void
  roleSelect(event: FormEvent<HTMLInputElement>): void
  disabled: boolean
  invalid?: boolean
}

const IdentityFormComponent = ({
  handleSubmit,
  roleSelect,
  disabled,
  invalid
}: IdentityFormProps) => (
  <form noValidate>
    <Card>
      <CardHeader>Contact Details</CardHeader>
      <PageInformation id="identity-text">
        We may need to get in touch with you. Tell us a bit more about yourself.
      </PageInformation>
      <Field
        id="identityName"
        name="identityName"
        label="Your Name or Business"
        autoFocus={true} // eslint-disable-line jsx-a11y/no-autofocus
        type="text"
        required={true}
        normalize={normalize50}
        component={renderInput}
      />
      <br />
      <Field
        id="identityEmail"
        name="identityEmail"
        label="Email"
        type="email"
        autoCapitalize="off"
        autoCorrect="off"
        autoComplete="email"
        required={true}
        normalize={normalizeEmail}
        component={renderInput}
      />
      <br />
      <Field
        id="roleInClaim"
        name="roleInClaim"
        label="Role"
        type="select"
        required={true}
        helpHeader={roleHelperHeaderText}
        helpText={roleHelp}
        component={renderSelect}
        data-trigger-modal="warningAuto-modal"
        onChange={roleSelect}
      >
        <option value="">Select Option</option>
        <option value="travelersPolicyHolder">Travelers Policyholder</option>
        <option value="claimant">Claimant</option>
        <option value="employee">Employee</option>
        <option value="medicalHealth">Medical Provider</option>
        <option value="repairCont">Repairer or Contractor </option>
        <option value="independentAdjuster">Independent Adjuster</option>
        <option value="hiredExpert">Hired Expert</option>
        <option value="legalRep">Legal Representative</option>
        <option value="agentBroker">Agent or Broker</option>
        <option value="otherCarrier">Insurance Company</option>
        <option value="cssCustomer">CSS Customer</option>
        <option value="other">Other</option>
      </Field>
    </Card>
    <ButtonContainer>
      <ContinueButton handleClick={handleSubmit} type='submit' disabled={invalid||disabled} />
      <SecondaryButton
        data-trigger-modal="warning-modal"
        type="button"
        float="left"
      >
        Back
      </SecondaryButton>
    </ButtonContainer>
  </form >
)

const validEmailMsg = 'Please enter a valid email address.'

const validEmailCheck =
  // eslint-disable-next-line max-len
  valid(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  validEmailMsg
)

const notEmptyWithValidEmailInput = validator(required(validEmailMsg), validEmailCheck)
const notEmptyWithValidNameInput = validator(
  required('Please enter a valid name or business.'),
  maxSize(50, 'Must be 50 characters or less.')
)
const validate = (values: IdentityFormType) => ({
  identityName: notEmptyWithValidNameInput(values.identityName),
  identityEmail: notEmptyWithValidEmailInput(values.identityEmail),
  roleInClaim: required('Please select a role.')(values.roleInClaim)
})

const IdentityForm = reduxForm<IdentityFormType, IdentityFormProps>({
  form: 'identityForm',
  validate,
  destroyOnUnmount: false,
  touchOnChange: true,
  touchOnBlur: true
})(IdentityFormComponent)

export {
  IdentityForm
}
